import { useState, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Send, Check, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useToast } from '@/hooks/use-toast';
import { useRateLimiter } from '@/hooks/useRateLimiter';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { createQuickLead } from '@/lib/leads';

const contactSchema = z.object({
  name: z.string().trim().min(2, 'Please enter your name').max(100, 'Name must be less than 100 characters'),
  email: z.string().trim().email('Please enter a valid email').max(255, 'Email must be less than 255 characters'),
  organization: z.string().trim().max(200, 'Organization name must be less than 200 characters').optional(),
  message: z.string().trim().min(1, 'Message is required').max(2000, 'Message must be less than 2000 characters'),
});

type ContactFormData = z.infer<typeof contactSchema>;

export const Contact = forwardRef<HTMLElement>((_, forwardedRef) => {
  const { ref, isVisible } = useScrollAnimation();
  const { toast } = useToast();
  const { checkRateLimit, recordSubmission } = useRateLimiter({
    minInterval: 5000,  // 5 seconds between submissions
    maxAttempts: 5,     // Max 5 attempts
    windowMs: 300000,   // Within 5 minutes
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    organization: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Check rate limit before proceeding
    const rateLimitCheck = checkRateLimit();
    if (!rateLimitCheck.allowed) {
      toast({
        title: 'Please slow down',
        description: rateLimitCheck.message,
        variant: 'destructive',
      });
      return;
    }

    // Validate form data
    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ContactFormData, string>> = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof ContactFormData;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    recordSubmission(); // Record the submission attempt

    try {
      await createQuickLead({
        full_name: result.data.name,
        email: result.data.email,
        organization_name: result.data.organization || null,
        message: result.data.message,
      });

      setIsSuccess(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
    
    toast({
      title: 'Message sent!',
      description: "We'll be in touch within 1-2 business days.",
    });
  };

  if (isSuccess) {
    return (
      <section id="contact" className="py-16 sm:py-20 md:py-24 lg:py-32 bg-background">
        <div ref={ref} className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-xl mx-auto text-center"
          >
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <Check className="w-7 h-7 sm:w-8 sm:h-8 text-gold" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 sm:mb-4">
              Message Received
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              We'll be in touch within 1-2 business days.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-10 sm:py-12 md:py-14 lg:py-16 bg-background overflow-hidden">
      <div ref={ref} className="container mx-auto px-5 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-start">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="text-gold font-semibold text-xs sm:text-sm tracking-widest uppercase mb-2 sm:mb-3 block">
              Get in Touch
            </span>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-foreground tracking-tight mb-3 sm:mb-4">
              Ready to Elevate Your{' '}
              <span className="text-primary-deep block sm:inline">Agency's Reach?</span>
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-5 sm:mb-6">
              Let's discuss how Yaw CTV can help your agency achieve its marketing
              objectives. Looking for a full-service partner or specific capabilities? 
              We're here to help.
            </p>
            
            <div className="space-y-2.5 sm:space-y-3">
              <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <div className="w-1.5 h-1.5 bg-gold rounded-full flex-shrink-0" />
                <span>SDVOSB & MBO Certified Partner</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <div className="w-1.5 h-1.5 bg-gold rounded-full flex-shrink-0" />
                <span>Response within 1-2 business days</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <div className="w-1.5 h-1.5 bg-gold rounded-full flex-shrink-0" />
                <span>Free initial consultation</span>
              </div>
            </div>
          </motion.div>

          {/* Right Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: isVisible ? 0.1 : 0, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={handleSubmit}
            className="space-y-4 bg-card p-5 sm:p-6 rounded-lg border border-border"
          >
            <div>
              <Label htmlFor="name" className="text-foreground text-sm font-medium">
                Name *
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                className={`mt-1.5 border-border bg-background focus:border-primary-deep focus:ring-primary-deep ${
                  errors.name ? 'border-destructive' : ''
                }`}
              />
              {errors.name && (
                <p className="text-destructive text-xs mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email" className="text-foreground text-sm font-medium">
                Email *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.gov"
                className={`mt-1.5 border-border bg-background focus:border-primary-deep focus:ring-primary-deep ${
                  errors.email ? 'border-destructive' : ''
                }`}
              />
              {errors.email && (
                <p className="text-destructive text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <Label htmlFor="organization" className="text-foreground text-sm font-medium">
                Agency / Organization
              </Label>
              <Input
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                placeholder="Your agency or organization"
                className="mt-1.5 border-border bg-background focus:border-primary-deep focus:ring-primary-deep"
              />
            </div>

            <div>
              <Label htmlFor="message" className="text-foreground text-sm font-medium">
                Message *
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your project or needs..."
                rows={4}
                className={`mt-1.5 border-border bg-background focus:border-primary-deep focus:ring-primary-deep resize-none ${
                  errors.message ? 'border-destructive' : ''
                }`}
              />
              {errors.message && (
                <p className="text-destructive text-xs mt-1">{errors.message}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gold hover:bg-gold-light text-dark-bg font-semibold py-5 sm:py-6 text-sm sm:text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gold/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Send Message
                  <Send className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <div className="text-center pt-4 border-t border-border mt-4">
              <p className="text-sm text-muted-foreground mb-2">Want to share more details?</p>
              <Link 
                to="/contact" 
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:text-gold-light transition-colors"
              >
                Complete our campaign requirements form
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
});

Contact.displayName = 'Contact';
